/**
 * Allowed embedding/LMS origins of a bot (bot setting `postMessageTargetOrigin`, #565).
 *
 * Stored as a comma-separated string (the SCORM/fusion templates split it on ","). Used by the server (save path,
 * migration, frame-ancestors), the admin list editor and the bot frontend, so all of them normalize identically.
 *
 * - `*` anywhere → unrestricted ("*")
 * - bare host names get `https://` (e.g. `reflact-train.com` → `https://reflact-train.com`)
 * - paths, queries and trailing slashes are cut to the origin; hosts are lowercased; duplicates removed
 * - entries that are no http(s) origin (`file://…`, test input like `asd`) are dropped
 * - nothing left → "" (not configured: behaves like today, i.e. unrestricted)
 */

export type EmbedOrigins = { mode: "unrestricted" } | { mode: "list"; origins: string[] };

const HOST_LIKE = /^(localhost|[a-z0-9-]+(\.[a-z0-9-]+)+|\d{1,3}(\.\d{1,3}){3})(:\d+)?([/?#].*)?$/i;

/** Normalizes one entry to an origin, or null if it is not a usable http(s) origin. */
export function normalizeEmbedOrigin(entry: string): string | null {
  let value = entry.trim();
  if (!value) return null;
  if (!/^[a-z][a-z0-9+.-]*:/i.test(value)) {
    if (!HOST_LIKE.test(value)) return null;
    value = "https://" + value;
  }
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  if (!url.hostname) return null;
  return url.origin.toLowerCase();
}

export function parseEmbedOrigins(value: string | null | undefined): EmbedOrigins {
  const entries = String(value ?? "").split(",").map((e) => e.trim()).filter(Boolean);
  if (entries.includes("*")) return { mode: "unrestricted" };
  const origins: string[] = [];
  for (const entry of entries) {
    const origin = normalizeEmbedOrigin(entry);
    if (origin != null && !origins.includes(origin)) origins.push(origin);
  }
  return origins.length > 0 ? { mode: "list", origins } : { mode: "unrestricted" };
}

/** Normalized storage value: "*", "" or "https://a,https://b". */
export function normalizeEmbedOriginsValue(value: string | null | undefined): string {
  const raw = String(value ?? "").trim();
  if (raw === "") return "";
  const parsed = parseEmbedOrigins(raw);
  if (parsed.mode === "list") return parsed.origins.join(",");
  return raw.split(",").map((e) => e.trim()).includes("*") ? "*" : "";
}
